import React, { useEffect, useState } from "react";
import { api, RSD, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";
import { Percent, Power, AlertTriangle, TrendingUp } from "lucide-react";

const localInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function Stat({ label, value, tone = "" }) {
  return (
    <div className="bg-white rounded-xl border p-2.5">
      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`font-display font-black ${tone}`}>{value}</p>
    </div>
  );
}

function Analytics({ rid, promoId }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get(`/restaurant/${rid}/store-promotion/${promoId}/analytics`)
      .then(({ data }) => setA(data)).catch(() => setA(null));
  }, [rid, promoId]);
  if (!a) return null;
  return (
    <div data-testid="store-promo-analytics" className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
      <Stat label="Porudžbine" value={a.orders} />
      <Stat label="Prodato komada" value={a.units} />
      <Stat label="Originalna vrednost" value={RSD(a.gross_value)} />
      <Stat label="Dat popust" value={RSD(a.discount_given)} tone="text-brand-red" />
      <Stat label="Finalni prihod" value={RSD(a.final_revenue)} tone="text-brand-green" />
      <Stat label="Prosečna porudžbina" value={RSD(a.avg_order_value)} />
    </div>
  );
}

/** Restaurant-wide percentage discount. All state and pricing come from the backend. */
export default function StorePromoCard({ rid, canManage, sampleProduct }) {
  const [data, setData] = useState(null);
  const [percent, setPercent] = useState(20);
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = () => api.get(`/restaurant/${rid}/store-promotion`).then(({ data }) => {
    setData(data);
    setPercent(data.promotion?.discount_percent || data.default_percent);
    setStarts(localInput(data.promotion?.starts_at));
    setEnds(localInput(data.promotion?.ends_at));
  }).catch(() => setData(null));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

  const promo = data?.promotion;
  const running = !!promo?.running_now;
  const base = Number(sampleProduct?.price) || 1000;

  const save = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/restaurant/${rid}/store-promotion`, {
        discount_percent: percent,
        starts_at: starts ? new Date(starts).toISOString() : null,
        ends_at: ends ? new Date(ends).toISOString() : null,
      });
      setEditing(false);
      await load();
      toast.success(res.promotion.running_now ? `Popust ${res.promotion.discount_percent}% na sve je aktivan` : "Promocija je zakazana");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "object" ? d.message : d) || "Greška");
    } finally { setBusy(false); }
  };

  const stop = async () => {
    if (!window.confirm("Da li ste sigurni da želite da isključite popust na sve?")) return;
    setBusy(true);
    try {
      await api.post(`/restaurant/${rid}/store-promotion/deactivate`);
      await load();
      toast.success("Promocija je isključena");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "object" ? d.message : d) || "Greška");
    } finally { setBusy(false); }
  };

  if (!data) return null;

  return (
    <div data-testid="store-promo-card" className={`rounded-2xl border-2 p-4 mb-5 ${running ? "border-brand-green bg-brand-green/5" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="mr-auto">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Popust na sve</p>
          {running ? (
            <p data-testid="store-promo-status" className="font-display font-black text-xl text-brand-green flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />AKTIVNO{" "}
              <span data-testid="store-promo-percent" className="text-slate-900">{promo.discount_percent}%</span>
            </p>
          ) : promo ? (
            <p data-testid="store-promo-status" className="font-display font-black text-xl text-brand-orange">
              ZAKAZANO · {promo.discount_percent}%
            </p>
          ) : (
            <p data-testid="store-promo-status" className="font-display font-black text-xl text-slate-500">NIJE AKTIVNO</p>
          )}
          <p className="text-sm text-slate-500 mt-0.5">
            {promo
              ? <>Početak: <b>{fmtDateTime(promo.starts_at)}</b> · Kraj: <b>{promo.ends_at ? fmtDateTime(promo.ends_at) : "bez vremena završetka"}</b></>
              : "Primeni popust na sve proizvode jednim klikom."}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <button data-testid="store-promo-edit" onClick={() => setEditing((v) => !v)}
              className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-display font-black text-slate-700">
              {promo ? "IZMENI" : "AKTIVIRAJ"}
            </button>
            {promo && (
              <button data-testid="store-promo-stop" disabled={busy} onClick={stop}
                className="flex items-center gap-1.5 rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-display font-black disabled:opacity-50">
                <Power size={14} />DEAKTIVIRAJ
              </button>
            )}
          </div>
        )}
      </div>

      {editing && canManage && (
        <div data-testid="store-promo-form" className="mt-4 bg-white rounded-2xl border p-4 space-y-3">
          <div>
            <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Procenat popusta</p>
            <div className="flex flex-wrap gap-2">
              {data.allowed_percents.map((p) => (
                <button key={p} data-testid={`store-promo-pct-${p}`} onClick={() => setPercent(p)}
                  className={`w-14 h-12 rounded-xl font-display font-black border-2 ${percent === p ? "border-brand-red bg-brand-red text-white" : "border-slate-200 text-slate-600"}`}>
                  {p}%
                </button>
              ))}
            </div>
          </div>
          <div data-testid="store-promo-preview" className="flex items-center gap-2 bg-[#FAFAF8] rounded-xl p-3">
            <Percent size={15} className="text-brand-red" />
            <span className="text-sm font-bold">POPUST NA SVE {percent}%</span>
            <span className="ml-auto text-sm">
              <span className="line-through text-slate-400">{RSD(base)}</span>{" → "}
              <b data-testid="store-promo-preview-price">{RSD(Math.round(base * (1 - percent / 100) * 100) / 100)}</b>
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Početak (opciono)</span>
              <input data-testid="store-promo-starts" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)}
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
            <label className="block"><span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Kraj (prazno = bez završetka)</span>
              <input data-testid="store-promo-ends" type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)}
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
          </div>
          {data.happy_hour_count > 0 && (
            <p data-testid="store-promo-hh-warning" className="flex gap-2 text-sm font-bold text-brand-orange">
              <AlertTriangle size={16} className="shrink-0" />
              Postoje aktivne Happy Hour promocije. One će biti privremeno nadjačane dok traje popust na sve.
            </p>
          )}
          <p className="text-xs text-slate-400">
            Popust se primenjuje na baznu cenu proizvoda (ne na plaćene dodatke) i samo na porudžbine kupaca sa Ponesi.rs —
            Vista zadržava interne cene. Bazne cene se ne menjaju i vraćaju se automatski kada promocija prestane.
          </p>
          <div className="flex gap-2">
            <button data-testid="store-promo-save" disabled={busy} onClick={save}
              className="flex-1 rounded-xl bg-brand-red text-white py-3.5 font-display font-black disabled:opacity-50">
              {busy ? "ČUVAM…" : promo ? "SAČUVAJ" : `AKTIVIRAJ ${percent}% NA SVE`}
            </button>
            <button onClick={() => setEditing(false)} className="px-4 rounded-xl bg-slate-100 font-bold">Otkaži</button>
          </div>
        </div>
      )}

      {promo && <Analytics rid={rid} promoId={promo.id} />}

      {data.history?.length > 1 && (
        <details data-testid="store-promo-history" className="mt-3">
          <summary className="text-xs font-black uppercase tracking-wider text-slate-400 cursor-pointer">
            <TrendingUp size={12} className="inline mr-1" />Istorija promocija ({data.history.length})
          </summary>
          <div className="mt-2 space-y-1">
            {data.history.map((h) => (
              <div key={h.id} data-testid={`store-promo-hist-${h.id}`} className="flex flex-wrap items-center gap-2 text-sm bg-white rounded-xl border p-2.5">
                <span className="font-black">{h.discount_percent}% NA SVE</span>
                <span className="text-slate-400 text-xs">
                  {fmtDateTime(h.starts_at)} – {h.ends_at ? fmtDateTime(h.ends_at) : (h.closed_at ? fmtDateTime(h.closed_at) : "—")}
                </span>
                <span className={`ml-auto text-[10px] font-black uppercase rounded-full px-2 py-1 ${h.running_now ? "bg-brand-green/10 text-brand-green" : "bg-slate-100 text-slate-500"}`}>
                  {h.running_now ? "Aktivno" : h.status === "SCHEDULED" ? "Zakazano" : "Završeno"}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
